// Apps list — list, create, rename, delete app records.

import React from "react";
import {
  Alert,
  Box,
  Button,
  Card,
  CardContent,
  Chip,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Divider,
  FormControl,
  FormControlLabel,
  IconButton,
  InputAdornment,
  Menu,
  MenuItem,
  Pagination,
  Radio,
  RadioGroup,
  Snackbar,
  Stack,
  Switch,
  TextField,
  Tooltip,
  Typography,
} from "@mui/material";
import {
  APP_SHARING_PRIVATE,
  APP_SHARING_SHARED,
  APP_SHARING_PUBLIC,
  APP_TYPE_INTERNAL,
  APP_TYPE_EXTERNAL,
} from "../../constants";
import ChatInput from "../../components/ChatInput";
import IconPickerDialog from "../../components/IconPickerDialog";
import LucideIcon from "../../components/LucideIcon";
import { useDictation } from "../../hooks/useDictation";
import type { BuilderApiClient } from "./api";
import type { BuilderApp, GitSyncStatus, PublishStatusResponse } from "./types";
import GitLinkDialog from "./dialogs/GitLinkDialog";

const DEFAULT_APP_ICON = "AppWindowMac";
const PAGE_SIZE = 24;
const SEARCH_DEBOUNCE_MS = 300;
const UNTITLED_APP_NAME = "Untitled app";

// Example prompts shown under the hero input on the empty state. Mix of
// internal-team tools and external customer-facing apps so the suggestions
// reflect both app types now supported.
const HERO_SUGGESTIONS = [
  "An internal CRM with companies, contacts, and deal stages",
  "A customer support inbox that tracks tickets to resolution",
  "A public job board where candidates can apply",
  "A client portal where customers sign in to view invoices",
  "An onboarding checklist tool for new hires",
  "A booking page where customers schedule a 30-min call",
];

interface Props {
  api: BuilderApiClient;
  companyId: string;
  getToken: () => Promise<string>;
  // `seedPrompt` is set when the user submits the empty-state hero input —
  // the parent forwards it into BuilderEditor so the agent gets it as the
  // first message of the new app's thread.
  onOpen?: (id: string, seedPrompt?: string) => void;
  openDialog: (title: string, content: string, buttonText: string, onClick: () => void) => void;
  currentUid?: string;
  menuAppIds: string[];
  onMenuAppIdsChange: (ids: string[]) => void;
}

const SHARING_LABEL: Record<string, string> = {
  [APP_SHARING_PRIVATE]: "Private",
  [APP_SHARING_SHARED]: "Shared with company",
  [APP_SHARING_PUBLIC]: "Public",
};

const SHARING_DESCRIPTION: Record<string, string> = {
  [APP_SHARING_PRIVATE]: "Only you can open or edit this app.",
  [APP_SHARING_SHARED]: "Anyone in your company can open and edit this app.",
  [APP_SHARING_PUBLIC]: "Anyone with the link can open this app.",
};

const APP_TYPE_LABEL: Record<string, string> = {
  [APP_TYPE_INTERNAL]: "Internal app",
  [APP_TYPE_EXTERNAL]: "External app",
};

const APP_TYPE_DESCRIPTION: Record<string, string> = {
  [APP_TYPE_INTERNAL]:
    "Uses platform login. Can be used inside the platform.",
  [APP_TYPE_EXTERNAL]:
    "Public signup page. Anyone can sign up.",
};

const publishMenuLabel = (status: PublishStatusResponse | null): string => {
  switch (status?.publishStatus) {
    case "building":
      return "Publishing — building…";
    case "queued":
      return "Publishing — queued…";
    default:
      return status?.publishedBuildId ? "Publish a new version" : "Publish";
  }
};

const externalAppUrl = (appId: string) =>
  `${window.location.origin}/x/${appId}`;

const openExternalApp = (appId: string) =>
  window.open(`/x/${appId}`, "_blank", "noopener");

const copyToClipboard = async (text: string) => {
  try {
    await navigator.clipboard.writeText(text);
  } catch {
    /* clipboard blocked — best effort */
  }
};

const AppsList: React.FC<Props> = ({
  api,
  companyId,
  getToken,
  onOpen,
  openDialog,
  currentUid,
  menuAppIds,
  onMenuAppIdsChange,
}) => {
  const [apps, setApps] = React.useState<BuilderApp[] | null>(null);
  const [total, setTotal] = React.useState(0);
  const [page, setPage] = React.useState(1);
  const [error, setError] = React.useState<string | null>(null);

  // Search input state. `searchInput` is the live text in the field;
  // `searchQuery` is the debounced value actually sent to the backend so we
  // don't fire a request on every keystroke.
  const [searchInput, setSearchInput] = React.useState("");
  const [searchQuery, setSearchQuery] = React.useState("");
  const [loading, setLoading] = React.useState(false);

  // Create dialog
  const [createOpen, setCreateOpen] = React.useState(false);
  const [creating, setCreating] = React.useState(false);
  const [createDraft, setCreateDraft] = React.useState({
    name: "",
    description: "",
    icon: DEFAULT_APP_ICON,
    appType: APP_TYPE_INTERNAL,
  });

  // Hero state has its own type selector since it submits without opening the
  // Create dialog; mirrors the field in `createDraft`.
  const [heroAppType, setHeroAppType] = React.useState<string>(APP_TYPE_INTERNAL);
  const [heroTypeMenuAnchor, setHeroTypeMenuAnchor] =
    React.useState<HTMLElement | null>(null);

  // Empty-state hero input (only shown when the user has zero apps and no
  // search query). Submitting creates an "Untitled app" and drops the user
  // into the editor with this prompt as the first chat message.
  const [heroPrompt, setHeroPrompt] = React.useState("");
  const [heroSubmitting, setHeroSubmitting] = React.useState(false);

  const {
    isRecording: heroIsDictating,
    isLoading: heroDictationLoading,
    startDictation: heroStartDictation,
    stopDictation: heroStopDictation,
  } = useDictation({
    onTranscript: (text, isFinal) => {
      if (isFinal && text.trim()) {
        setHeroPrompt((prev) => {
          const trimmedPrev = prev.trim();
          if (trimmedPrev) {
            return trimmedPrev + " " + text.trim();
          }
          return text.trim();
        });
      }
    },
  });

  // Edit dialog
  const [editing, setEditing] = React.useState<BuilderApp | null>(null);
  const [editDraft, setEditDraft] = React.useState({
    name: "",
    description: "",
    sharing: APP_SHARING_PRIVATE,
    icon: DEFAULT_APP_ICON,
  });
  const [savingEdit, setSavingEdit] = React.useState(false);

  // Icon picker — single dialog, but it can be opened from either the Create
  // or Edit dialog. `iconTarget` records which draft to write the selection
  // back into.
  const [iconPickerOpen, setIconPickerOpen] = React.useState(false);
  const [iconTarget, setIconTarget] = React.useState<"create" | "edit" | null>(null);

  // Row menu
  const [menuApp, setMenuApp] = React.useState<BuilderApp | null>(null);
  const [menuAnchor, setMenuAnchor] = React.useState<HTMLElement | null>(null);
  const [gitLinkApp, setGitLinkApp] = React.useState<BuilderApp | null>(null);
  // Status for the app currently shown in the More-menu. Fetched lazily when
  // the menu opens so we can show the right git actions (Connect vs Sync/Pull).
  const [gitStatus, setGitStatus] = React.useState<GitSyncStatus | null>(null);
  const [gitBusy, setGitBusy] = React.useState<"push" | "pull" | "unlink" | null>(null);
  // Same lazy-fetched pattern for publish: loaded when the menu opens so the
  // MenuItem can show Live/Building/Failed inline.
  const [publishStatus, setPublishStatus] = React.useState<PublishStatusResponse | null>(null);
  const [snack, setSnack] = React.useState<{ msg: string; severity: "success" | "error" | "info" } | null>(null);

  const openMenuForApp = React.useCallback(
    (app: BuilderApp, anchor: HTMLElement) => {
      setMenuApp(app);
      setMenuAnchor(anchor);
      setGitStatus(null);
      setPublishStatus(null);
      api
        .getGitStatus(app.id)
        .then(setGitStatus)
        .catch(() => {
          // Status is non-critical; if it fails the menu just shows "Connect".
          setGitStatus({
            linked: false,
            syncInProgress: false,
            drift: 0,
          });
        });
      api
        .getPublishStatus(app.id)
        .then(setPublishStatus)
        .catch(() => {
          // Same story — falls back to a generic "Publish" item.
          setPublishStatus(null);
        });
    },
    [api],
  );

  const closeMenu = React.useCallback(() => {
    setMenuAnchor(null);
    setMenuApp(null);
    setGitStatus(null);
    setPublishStatus(null);
  }, []);

  const runPublish = React.useCallback(
    async (app: BuilderApp) => {
      try {
        await api.publishApp(app.id);
        setSnack({
          msg: "Publish started — bundling…",
          severity: "info",
        });
      } catch (e: any) {
        setSnack({
          msg: e?.response?.data?.error || e?.message || "Publish failed",
          severity: "error",
        });
      }
    },
    [api],
  );

  const runGitAction = React.useCallback(
    async (kind: "push" | "pull" | "unlink", app: BuilderApp) => {
      setGitBusy(kind);
      try {
        if (kind === "push") {
          await api.pushToGit(app.id);
          setSnack({ msg: "Sync started — pushing local changes to GitHub", severity: "info" });
        } else if (kind === "pull") {
          await api.pullFromGit(app.id);
          setSnack({ msg: "Pull started — fetching changes from GitHub", severity: "info" });
        } else {
          await api.unlinkGitRepo(app.id);
          setSnack({ msg: "Unlinked from GitHub", severity: "success" });
        }
      } catch (e: any) {
        setSnack({
          msg: e?.response?.data?.error || e?.message || `${kind} failed`,
          severity: "error",
        });
      } finally {
        setGitBusy(null);
      }
    },
    [api],
  );

  const load = React.useCallback(
    async (opts?: { search?: string; page?: number }) => {
      const targetPage = opts?.page ?? page;
      const targetSearch = opts?.search ?? searchQuery;
      setLoading(true);
      try {
        const result = await api.listApps({
          search: targetSearch || undefined,
          limit: PAGE_SIZE,
          offset: (targetPage - 1) * PAGE_SIZE,
        });
        setApps(result.apps);
        setTotal(result.total);
        setError(null);
      } catch (e: any) {
        setError(String(e?.response?.data?.error || e?.message || e));
      } finally {
        setLoading(false);
      }
    },
    [api, page, searchQuery],
  );

  // Debounce the search input so each keystroke doesn't fire a request.
  React.useEffect(() => {
    const handle = window.setTimeout(() => {
      setSearchQuery(searchInput.trim());
    }, SEARCH_DEBOUNCE_MS);
    return () => window.clearTimeout(handle);
  }, [searchInput]);

  // When the debounced query changes, snap back to page 1 so the user sees
  // the top of the new result set.
  React.useEffect(() => {
    setPage(1);
  }, [searchQuery]);

  React.useEffect(() => {
    load();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [api, page, searchQuery]);

  const totalPages = Math.max(1, Math.ceil(total / PAGE_SIZE));

  const onCreate = async () => {
    if (!createDraft.name.trim()) return;
    setCreating(true);
    try {
      const created = await api.createApp({
        name: createDraft.name.trim(),
        description: createDraft.description.trim() || undefined,
        icon: createDraft.icon || DEFAULT_APP_ICON,
        appType: createDraft.appType,
      });
      setCreateOpen(false);
      setCreateDraft({
        name: "",
        description: "",
        icon: DEFAULT_APP_ICON,
        appType: APP_TYPE_INTERNAL,
      });
      await load();
      // Drop straight into the builder so the user can chat with the AI.
      if (onOpen && created?.id) onOpen(created.id);
    } catch (e: any) {
      setError(String(e?.response?.data?.error || e?.message || e));
    } finally {
      setCreating(false);
    }
  };

  const submitHeroPrompt = async () => {
    const text = heroPrompt.trim();
    if (!text || heroSubmitting) return;
    setHeroSubmitting(true);
    try {
      // Pass the prompt as `seedPrompt` so the backend can synchronously
      // generate a real app name before responding — the editor then opens
      // with the correct title already in place.
      const created = await api.createApp({
        name: UNTITLED_APP_NAME,
        icon: DEFAULT_APP_ICON,
        appType: heroAppType,
        seedPrompt: text,
      });
      setHeroPrompt("");
      await load();
      if (onOpen && created?.id) onOpen(created.id, text);
    } catch (e: any) {
      setError(String(e?.response?.data?.error || e?.message || e));
    } finally {
      setHeroSubmitting(false);
    }
  };

  const isOwner = (a: BuilderApp | null) =>
    !!(a && currentUid && a.uid === currentUid);

  // Only external apps can be published — internal apps run inside the
  // platform shell and have no buildable Vite project. Mirrors the
  // backend's appType gate in /apps/<id>/publish.
  const isPublishable = (a: BuilderApp | null) =>
    isOwner(a) && a?.appType === APP_TYPE_EXTERNAL;

  const onSaveEdit = async () => {
    if (!editing) return;
    setSavingEdit(true);
    try {
      const patch: Partial<BuilderApp> = {
        name: editDraft.name,
        description: editDraft.description,
        icon: editDraft.icon || DEFAULT_APP_ICON,
      };
      // Only owners can change sharing — backend enforces this too, but we
      // skip sending it from non-owners to avoid a 404 on an otherwise valid
      // name/description edit (which non-owners can perform on Shared apps).
      if (isOwner(editing) && editDraft.sharing !== editing.sharing) {
        patch.sharing = editDraft.sharing;
      }
      await api.patchApp(editing.id, patch);
      setEditing(null);
      await load();
    } catch (e: any) {
      setError(String(e?.response?.data?.error || e?.message || e));
    } finally {
      setSavingEdit(false);
    }
  };

  const isInMenu = (id: string) => menuAppIds.includes(id);

  const toggleInMenu = (a: BuilderApp) => {
    const next = isInMenu(a.id)
      ? menuAppIds.filter((x) => x !== a.id)
      : [...menuAppIds, a.id];
    onMenuAppIdsChange(next);
  };

  const exportApp = async (a: BuilderApp) => {
    try {
      const blob = await api.exportApp(a.id);
      const url = URL.createObjectURL(blob);
      const safeName = (a.name || "app").replace(/[^A-Za-z0-9_-]+/g, "-").replace(/^-+|-+$/g, "") || "app";
      const link = document.createElement("a");
      link.href = url;
      link.download = `${safeName}.zip`;
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
      URL.revokeObjectURL(url);
      setSnack({ msg: "Export downloaded", severity: "success" });
    } catch (e: any) {
      setSnack({
        msg: `Export failed: ${e?.response?.data?.error || e?.message || e}`,
        severity: "error",
      });
    }
  };

  const onDelete = (a: BuilderApp) => {
    openDialog(
      `Delete "${a.name}"?`,
      "This will permanently delete the app and all of its files, threads, and data.",
      "Delete",
      async () => {
        try {
          await api.deleteApp(a.id);
          await load();
        } catch (e: any) {
          setError(String(e?.response?.data?.error || e?.message || e));
        }
      },
    );
  };

  // True empty state — no apps and the user isn't searching. Shows a centered
  // hero input + suggestion chips instead of the regular list chrome.
  const isHeroState = apps !== null && apps.length === 0 && !searchQuery;

  if (isHeroState) {
    return (
      <Box
        sx={{
          p: 3,
          minHeight: "calc(100vh - 64px)",
          display: "flex",
          flexDirection: "column",
          alignItems: "center",
          justifyContent: "center",
        }}
      >
        {error && (
          <Alert
            severity="error"
            sx={{ mb: 2, width: "100%", maxWidth: 720 }}
            onClose={() => setError(null)}
          >
            {error}
          </Alert>
        )}
        <Box sx={{ width: "100%", maxWidth: 720, textAlign: "center" }}>
          <Typography
            variant="h3"
            sx={{ fontWeight: 700, mb: 2, letterSpacing: "-0.02em" }}
          >
            Turn your ideas into apps
          </Typography>
          <Typography variant="body1" color="text.secondary" sx={{ mb: 4 }}>
            Build internal tools for your team or external apps for your
            customers in minutes — just describe what you want. No coding
            necessary.
          </Typography>
          <Box sx={{ mb: 3, textAlign: "left" }}>
            <ChatInput
              value={heroPrompt}
              onChange={setHeroPrompt}
              onSend={submitHeroPrompt}
              placeholder="Design a personal CRM…"
              sending={heroSubmitting}
              disabled={heroSubmitting}
              rows={5}
              enablePopOut={false}
              inputId="apps-list-hero-input"
              showDictation
              isDictating={heroIsDictating}
              isDictationLoading={heroDictationLoading}
              onStartDictation={heroStartDictation}
              onStopDictation={heroStopDictation}
            />
            <Box
              sx={{
                display: "flex",
                justifyContent: "flex-start",
                mt: 1,
                px: 0.5,
              }}
            >
              <Button
                size="small"
                variant="text"
                disabled={heroSubmitting}
                onClick={(e) => setHeroTypeMenuAnchor(e.currentTarget)}
                startIcon={
                  heroAppType === APP_TYPE_EXTERNAL ? (
                    <LucideIcon name="Globe" size={16} />
                  ) : (
                    <LucideIcon name="Lock" size={16} />
                  )
                }
                endIcon={<LucideIcon name="ChevronDown" size={16} />}
                sx={{
                  color: "text.secondary",
                  textTransform: "none",
                  fontWeight: 500,
                }}
              >
                {APP_TYPE_LABEL[heroAppType]}
              </Button>
              <Menu
                anchorEl={heroTypeMenuAnchor}
                open={Boolean(heroTypeMenuAnchor)}
                onClose={() => setHeroTypeMenuAnchor(null)}
                anchorOrigin={{ vertical: "bottom", horizontal: "left" }}
                transformOrigin={{ vertical: "top", horizontal: "left" }}
              >
                {[APP_TYPE_INTERNAL, APP_TYPE_EXTERNAL].map((value) => (
                  <MenuItem
                    key={value}
                    selected={heroAppType === value}
                    onClick={() => {
                      setHeroAppType(value);
                      setHeroTypeMenuAnchor(null);
                    }}
                    sx={{ alignItems: "flex-start", py: 1.25, maxWidth: 360 }}
                  >
                    <Box
                      sx={{
                        display: "flex",
                        gap: 1.25,
                        alignItems: "flex-start",
                      }}
                    >
                      {value === APP_TYPE_EXTERNAL ? (
                        <Box sx={{ mt: 0.25, color: "text.secondary", display: "flex" }}>
                          <LucideIcon name="Globe" size={16} />
                        </Box>
                      ) : (
                        <Box sx={{ mt: 0.25, color: "text.secondary", display: "flex" }}>
                          <LucideIcon name="Lock" size={16} />
                        </Box>
                      )}
                      <Box>
                        <Typography variant="body2">
                          {APP_TYPE_LABEL[value]}
                        </Typography>
                        <Typography
                          variant="caption"
                          color="text.secondary"
                          sx={{ display: "block", whiteSpace: "normal" }}
                        >
                          {APP_TYPE_DESCRIPTION[value]}
                        </Typography>
                      </Box>
                    </Box>
                  </MenuItem>
                ))}
              </Menu>
            </Box>
          </Box>
          <Stack
            direction="row"
            spacing={1}
            justifyContent="center"
            sx={{ flexWrap: "wrap", gap: 1 }}
          >
            {HERO_SUGGESTIONS.map((s) => (
              <Chip
                key={s}
                label={s}
                onClick={() => setHeroPrompt(s)}
                disabled={heroSubmitting}
                sx={{ cursor: "pointer" }}
              />
            ))}
          </Stack>
        </Box>

        <IconPickerDialog
          open={iconPickerOpen}
          value={iconTarget === "edit" ? editDraft.icon : createDraft.icon}
          onClose={() => {
            setIconPickerOpen(false);
            setIconTarget(null);
          }}
          onSelect={(name) => {
            if (iconTarget === "edit") {
              setEditDraft((d) => ({ ...d, icon: name }));
            } else {
              setCreateDraft((d) => ({ ...d, icon: name }));
            }
          }}
        />
      </Box>
    );
  }

  return (
    <Box sx={{ p: 3 }}>
      <Stack
        direction="row"
        justifyContent="space-between"
        alignItems="center"
        sx={{ mb: 2 }}
      >
        <Box>
          <Typography variant="h5">Apps</Typography>
          <Typography variant="body2" color="text.secondary">
            Build internal tools and external apps with AI. Pick an app to keep editing, or start a new one.
          </Typography>
        </Box>
        <Button
          startIcon={<LucideIcon name="Plus" size={20} />}
          variant="contained"
          onClick={() => setCreateOpen(true)}
        >
          New App
        </Button>
      </Stack>

      {error && (
        <Alert severity="error" sx={{ mb: 2 }} onClose={() => setError(null)}>
          {error}
        </Alert>
      )}

      <TextField
        value={searchInput}
        onChange={(e) => setSearchInput(e.target.value)}
        placeholder="Search apps"
        size="small"
        fullWidth
        sx={{ mb: 2, maxWidth: 420 }}
        InputProps={{
          startAdornment: (
            <InputAdornment position="start">
              <LucideIcon name="Search" size={16} />
            </InputAdornment>
          ),
        }}
      />

      {apps === null ? (
        <Typography variant="body2" color="text.secondary">
          Loading…
        </Typography>
      ) : apps.length === 0 ? (
        <Card variant="outlined">
          <CardContent>
            <Typography variant="body1" gutterBottom>
              No apps match "{searchQuery}".
            </Typography>
            <Typography variant="body2" color="text.secondary">
              Try a different search.
            </Typography>
          </CardContent>
        </Card>
      ) : (
        <Box
          sx={{
            display: "grid",
            gridTemplateColumns: {
              xs: "1fr",
              sm: "1fr 1fr",
              md: "1fr 1fr 1fr",
            },
            gap: 2,
          }}
        >
          {apps.map((a) => (
            <Card
              key={a.id}
              variant="outlined"
              onClick={() => onOpen?.(a.id)}
              sx={{
                cursor: onOpen ? "pointer" : "default",
                transition: "border-color 0.15s, box-shadow 0.15s",
                "&:hover": onOpen
                  ? { borderColor: "primary.main", boxShadow: 1 }
                  : {},
              }}
            >
              <CardContent sx={{ position: "relative" }}>
                <Tooltip title="More">
                  <IconButton
                    size="small"
                    sx={{ position: "absolute", top: 8, right: 8 }}
                    onClick={(e) => {
                      e.stopPropagation();
                      openMenuForApp(a, e.currentTarget);
                    }}
                  >
                    <LucideIcon name="MoreVertical" size={20} />
                  </IconButton>
                </Tooltip>
                <Stack direction="row" spacing={1} alignItems="center" sx={{ pr: 4 }}>
                  <Box
                    sx={{
                      display: "flex",
                      alignItems: "center",
                      justifyContent: "center",
                      width: 32,
                      height: 32,
                      borderRadius: 1,
                      backgroundColor: "action.hover",
                      flexShrink: 0,
                    }}
                  >
                    <LucideIcon name={a.icon || DEFAULT_APP_ICON} size={20} />
                  </Box>
                  <Typography variant="h6" noWrap>
                    {a.name}
                  </Typography>
                </Stack>
                {a.description && (
                  <Typography
                    variant="body2"
                    color="text.secondary"
                    sx={{
                      display: "-webkit-box",
                      WebkitLineClamp: 2,
                      WebkitBoxOrient: "vertical",
                      overflow: "hidden",
                    }}
                  >
                    {a.description}
                  </Typography>
                )}
                <Stack direction="row" spacing={0.5} sx={{ mt: 1, flexWrap: "wrap", gap: 0.5 }}>
                  <Chip
                    size="small"
                    label={
                      a.sharing === APP_SHARING_PUBLIC
                        ? "Public"
                        : a.sharing === APP_SHARING_SHARED
                          ? "Shared"
                          : "Private"
                    }
                    color={
                      a.sharing === APP_SHARING_PRIVATE ? "default" : "primary"
                    }
                    variant="outlined"
                  />
                  <Chip
                    size="small"
                    label={a.appType === APP_TYPE_EXTERNAL ? "External" : "Internal"}
                    variant="outlined"
                  />
                  {isInMenu(a.id) && (
                    <Chip size="small" label="In menu" color="primary" />
                  )}
                </Stack>
              </CardContent>
            </Card>
          ))}
        </Box>
      )}

      {totalPages > 1 && (
        <Stack alignItems="center" sx={{ mt: 3 }}>
          <Pagination
            count={totalPages}
            page={page}
            onChange={(_, p) => setPage(p)}
            color="primary"
            disabled={loading}
          />
        </Stack>
      )}

      <Menu
        anchorEl={menuAnchor}
        open={!!menuAnchor}
        onClose={closeMenu}
      >
        <MenuItem
          onClick={() => {
            if (menuApp) {
              setEditing(menuApp);
              setEditDraft({
                name: menuApp.name,
                description: menuApp.description || "",
                sharing: menuApp.sharing || APP_SHARING_PRIVATE,
                icon: menuApp.icon || DEFAULT_APP_ICON,
              });
            }
            setMenuAnchor(null);
            setMenuApp(null);
          }}
        >
          Edit
        </MenuItem>
        {menuApp?.appType === APP_TYPE_EXTERNAL && [
          <MenuItem
            key="open-public"
            onClick={() => {
              if (menuApp) openExternalApp(menuApp.id);
              setMenuAnchor(null);
              setMenuApp(null);
            }}
          >
            Open public app
          </MenuItem>,
          <MenuItem
            key="copy-public"
            onClick={() => {
              if (menuApp) void copyToClipboard(externalAppUrl(menuApp.id));
              setMenuAnchor(null);
              setMenuApp(null);
            }}
          >
            Copy public link
          </MenuItem>,
        ]}
        <MenuItem
          onClick={() => {
            if (menuApp) toggleInMenu(menuApp);
            setMenuAnchor(null);
            setMenuApp(null);
          }}
        >
          {menuApp && isInMenu(menuApp.id) ? "Remove from menu" : "Add to menu"}
        </MenuItem>
        <MenuItem
          onClick={() => {
            if (menuApp) void exportApp(menuApp);
            setMenuAnchor(null);
            setMenuApp(null);
          }}
        >
          Export as zip
        </MenuItem>
        {isPublishable(menuApp) && <Divider />}
        {isPublishable(menuApp) && publishStatus?.publishedBuildId && publishStatus.publishedUrl && (
          <MenuItem disabled sx={{ opacity: 1, "&.Mui-disabled": { opacity: 1 } }}>
            <Stack sx={{ width: "100%" }}>
              <Typography variant="caption" color="text.secondary">
                Published
              </Typography>
              <Typography
                variant="caption"
                component="a"
                href={publishStatus.publishedUrl}
                target="_blank"
                rel="noopener noreferrer"
                sx={{ color: "primary.main", textDecoration: "none" }}
                onClick={(e) => e.stopPropagation()}
              >
                Open published URL
              </Typography>
            </Stack>
          </MenuItem>
        )}
        {isPublishable(menuApp) && publishStatus?.publishStatus === "failed" && publishStatus.lastPublishError && (
          <MenuItem disabled sx={{ opacity: 1, "&.Mui-disabled": { opacity: 1 } }}>
            <Stack sx={{ width: "100%" }}>
              <Typography variant="caption" color="error">
                Last publish failed
              </Typography>
              <Typography
                variant="caption"
                sx={{
                  display: "block",
                  whiteSpace: "pre-wrap",
                  fontFamily: "monospace",
                  fontSize: 11,
                  color: "text.secondary",
                  maxHeight: 120,
                  overflow: "auto",
                }}
              >
                {publishStatus.lastPublishError}
              </Typography>
            </Stack>
          </MenuItem>
        )}
        {isPublishable(menuApp) && (
          <MenuItem
            disabled={
              publishStatus?.publishStatus === "queued"
              || publishStatus?.publishStatus === "building"
            }
            onClick={() => {
              const target = menuApp;
              closeMenu();
              if (target) void runPublish(target);
            }}
          >
            {publishMenuLabel(publishStatus)}
          </MenuItem>
        )}
        {isPublishable(menuApp) && <Divider />}
        {menuApp && isOwner(menuApp) && !gitStatus && (
          <MenuItem disabled>Loading GitHub status…</MenuItem>
        )}
        {menuApp && isOwner(menuApp) && gitStatus && !gitStatus.linked && (
          <MenuItem
            onClick={() => {
              const target = menuApp;
              closeMenu();
              setGitLinkApp(target);
            }}
          >
            Connect GitHub
          </MenuItem>
        )}
        {menuApp && isOwner(menuApp) && gitStatus?.linked && (
          <MenuItem disabled sx={{ opacity: 1, "&.Mui-disabled": { opacity: 1 } }}>
            <Stack sx={{ width: "100%" }}>
              <Typography variant="caption" color="text.secondary">
                GitHub
              </Typography>
              <Typography variant="body2" noWrap>
                {gitStatus.owner}/{gitStatus.repo}
                <Typography component="span" color="text.secondary"> · {gitStatus.branch}</Typography>
              </Typography>
              {gitStatus.drift > 0 && (
                <Typography variant="caption" color="primary">
                  {gitStatus.drift} local edit{gitStatus.drift === 1 ? "" : "s"} since last sync
                </Typography>
              )}
              {gitStatus.pendingPrUrl && (
                <Typography
                  variant="caption"
                  component="a"
                  href={gitStatus.pendingPrUrl}
                  target="_blank"
                  rel="noopener noreferrer"
                  sx={{ color: "warning.main" }}
                  onClick={(e) => e.stopPropagation()}
                >
                  Conflicts opened a PR — open and merge
                </Typography>
              )}
            </Stack>
          </MenuItem>
        )}
        {menuApp && isOwner(menuApp) && gitStatus?.linked && (
          <MenuItem
            disabled={gitBusy !== null || gitStatus.syncInProgress}
            onClick={() => {
              const target = menuApp;
              closeMenu();
              void runGitAction("push", target);
            }}
          >
            Sync now (push to GitHub)
          </MenuItem>
        )}
        {menuApp && isOwner(menuApp) && gitStatus?.linked && (
          <MenuItem
            disabled={gitBusy !== null || gitStatus.syncInProgress}
            onClick={() => {
              const target = menuApp;
              closeMenu();
              void runGitAction("pull", target);
            }}
          >
            Pull from GitHub
          </MenuItem>
        )}
        {menuApp && isOwner(menuApp) && gitStatus?.linked && (
          <MenuItem
            disabled={gitBusy !== null}
            onClick={() => {
              const target = menuApp;
              closeMenu();
              openDialog(
                "Unlink GitHub repo?",
                `This disconnects "${gitStatus.owner}/${gitStatus.repo}" from this app. The GitHub repo itself isn't touched.`,
                "Unlink",
                () => void runGitAction("unlink", target),
              );
            }}
          >
            Unlink GitHub
          </MenuItem>
        )}
        {menuApp && isOwner(menuApp) && <Divider />}
        {menuApp && isOwner(menuApp) && (
          <MenuItem
            onClick={() => {
              if (menuApp) onDelete(menuApp);
              closeMenu();
            }}
            sx={{ color: "error.main" }}
          >
            Delete
          </MenuItem>
        )}
      </Menu>

      <Snackbar
        open={!!snack}
        autoHideDuration={6000}
        onClose={() => setSnack(null)}
        anchorOrigin={{ vertical: "bottom", horizontal: "center" }}
      >
        {snack ? (
          <Alert severity={snack.severity} onClose={() => setSnack(null)}>
            {snack.msg}
          </Alert>
        ) : undefined}
      </Snackbar>

      {gitLinkApp && (
        <GitLinkDialog
          open={!!gitLinkApp}
          onClose={() => setGitLinkApp(null)}
          api={api}
          appId={gitLinkApp.id}
          companyId={companyId}
          getToken={getToken}
          onLinked={() => setGitLinkApp(null)}
        />
      )}

      <Dialog
        open={createOpen}
        onClose={() => setCreateOpen(false)}
        maxWidth="sm"
        fullWidth
      >
        <DialogTitle sx={{ pr: 6 }}>
          Create an app
          <IconButton
            onClick={() => setCreateOpen(false)}
            sx={{ position: "absolute", right: 8, top: 8 }}
          >
            <LucideIcon name="X" size={20} />
          </IconButton>
        </DialogTitle>
        <DialogContent dividers>
          <Stack spacing={2}>
            <Typography variant="body2" color="text.secondary">
              Give your app a name. You'll drop straight into the builder where
              the AI can scaffold pages, forms, and tables for you.
            </Typography>
            <Stack direction="row" spacing={2} alignItems="center">
              <Box
                sx={{
                  display: "flex",
                  alignItems: "center",
                  justifyContent: "center",
                  width: 48,
                  height: 48,
                  borderRadius: 1,
                  border: 1,
                  borderColor: "divider",
                  backgroundColor: "action.hover",
                }}
              >
                <LucideIcon name={createDraft.icon} size={24} />
              </Box>
              <Box>
                <Typography variant="body2" color="text.secondary">
                  Icon: <strong>{createDraft.icon}</strong>
                </Typography>
                <Button
                  size="small"
                  onClick={() => {
                    setIconTarget("create");
                    setIconPickerOpen(true);
                  }}
                  sx={{ mt: 0.5 }}
                >
                  Choose Icon
                </Button>
              </Box>
            </Stack>
            <TextField
              autoFocus
              label="Name"
              placeholder="Leads dashboard"
              value={createDraft.name}
              onChange={(e) =>
                setCreateDraft({ ...createDraft, name: e.target.value })
              }
              fullWidth
            />
            <TextField
              label="Description (optional)"
              placeholder="What is this app for?"
              value={createDraft.description}
              onChange={(e) =>
                setCreateDraft({ ...createDraft, description: e.target.value })
              }
              multiline
              rows={2}
              fullWidth
            />

            <Divider />

            <Box>
              <Typography variant="subtitle2" gutterBottom>
                App type
              </Typography>
              <Typography variant="caption" color="text.secondary" sx={{ display: "block", mb: 1 }}>
                This can't be changed later.
              </Typography>
              <FormControl>
                <RadioGroup
                  value={createDraft.appType}
                  onChange={(e) =>
                    setCreateDraft({ ...createDraft, appType: e.target.value })
                  }
                >
                  {[APP_TYPE_INTERNAL, APP_TYPE_EXTERNAL].map((value) => (
                    <FormControlLabel
                      key={value}
                      value={value}
                      control={<Radio />}
                      label={
                        <Box>
                          <Typography variant="body2">{APP_TYPE_LABEL[value]}</Typography>
                          <Typography variant="caption" color="text.secondary">
                            {APP_TYPE_DESCRIPTION[value]}
                          </Typography>
                        </Box>
                      }
                      sx={{ alignItems: "flex-start", mb: 1 }}
                    />
                  ))}
                </RadioGroup>
              </FormControl>
            </Box>
          </Stack>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setCreateOpen(false)}>Cancel</Button>
          <Button
            variant="contained"
            startIcon={<LucideIcon name="Sparkles" size={20} />}
            disabled={creating || !createDraft.name.trim()}
            onClick={onCreate}
          >
            {creating ? "Creating…" : "Create & build"}
          </Button>
        </DialogActions>
      </Dialog>

      <Dialog
        open={!!editing}
        onClose={() => setEditing(null)}
        maxWidth="sm"
        fullWidth
      >
        <DialogTitle sx={{ pr: 6 }}>
          Edit app
          <IconButton
            onClick={() => setEditing(null)}
            sx={{ position: "absolute", right: 8, top: 8 }}
          >
            <LucideIcon name="X" size={20} />
          </IconButton>
        </DialogTitle>
        <DialogContent dividers>
          <Stack spacing={2}>
            <Stack direction="row" spacing={2} alignItems="center">
              <Box
                sx={{
                  display: "flex",
                  alignItems: "center",
                  justifyContent: "center",
                  width: 48,
                  height: 48,
                  borderRadius: 1,
                  border: 1,
                  borderColor: "divider",
                  backgroundColor: "action.hover",
                }}
              >
                <LucideIcon name={editDraft.icon} size={24} />
              </Box>
              <Box>
                <Typography variant="body2" color="text.secondary">
                  Icon: <strong>{editDraft.icon}</strong>
                </Typography>
                <Button
                  size="small"
                  onClick={() => {
                    setIconTarget("edit");
                    setIconPickerOpen(true);
                  }}
                  sx={{ mt: 0.5 }}
                >
                  Choose Icon
                </Button>
              </Box>
            </Stack>
            <TextField
              autoFocus
              label="Name"
              value={editDraft.name}
              onChange={(e) =>
                setEditDraft({ ...editDraft, name: e.target.value })
              }
              fullWidth
            />
            <TextField
              label="Description"
              value={editDraft.description}
              onChange={(e) =>
                setEditDraft({ ...editDraft, description: e.target.value })
              }
              multiline
              rows={2}
              fullWidth
            />

            {editing?.appType && (
              <Box>
                <Typography variant="subtitle2" gutterBottom>
                  App type
                </Typography>
                <Typography variant="body2">
                  {APP_TYPE_LABEL[editing.appType] || editing.appType}
                </Typography>
                <Typography variant="caption" color="text.secondary">
                  {APP_TYPE_DESCRIPTION[editing.appType]} App type is set at creation and can't be changed.
                </Typography>
                {editing.appType === APP_TYPE_EXTERNAL && (
                  <Box sx={{ mt: 2 }}>
                    <Typography variant="subtitle2" gutterBottom>
                      Public link
                    </Typography>
                    <Stack direction="row" spacing={1} alignItems="center">
                      <TextField
                        size="small"
                        value={externalAppUrl(editing.id)}
                        InputProps={{ readOnly: true }}
                        fullWidth
                      />
                      <Button
                        size="small"
                        onClick={() => copyToClipboard(externalAppUrl(editing.id))}
                      >
                        Copy
                      </Button>
                      <Button size="small" onClick={() => openExternalApp(editing.id)}>
                        Open
                      </Button>
                    </Stack>
                    <Typography variant="caption" color="text.secondary">
                      Share this link with end-users. They'll sign up directly
                      inside the app.
                    </Typography>
                  </Box>
                )}
              </Box>
            )}

            <Divider />

            <Box>
              <Typography variant="subtitle2" gutterBottom>
                Sharing
              </Typography>
              <FormControl disabled={!isOwner(editing)}>
                <RadioGroup
                  value={editDraft.sharing}
                  onChange={(e) =>
                    setEditDraft({ ...editDraft, sharing: e.target.value })
                  }
                >
                  {[APP_SHARING_PRIVATE, APP_SHARING_SHARED, APP_SHARING_PUBLIC].map(
                    (value) => (
                      <FormControlLabel
                        key={value}
                        value={value}
                        control={<Radio />}
                        label={SHARING_LABEL[value]}
                      />
                    ),
                  )}
                </RadioGroup>
              </FormControl>
              <Typography variant="body2" color="text.secondary">
                {SHARING_DESCRIPTION[editDraft.sharing]}
              </Typography>
              {!isOwner(editing) && (
                <Typography variant="caption" color="text.secondary" sx={{ mt: 1, display: "block" }}>
                  Only the app's owner can change sharing.
                </Typography>
              )}
            </Box>

            {editing && (
              <Box>
                <FormControlLabel
                  control={
                    <Switch
                      checked={isInMenu(editing.id)}
                      onChange={() => toggleInMenu(editing)}
                    />
                  }
                  label="Show in my side menu"
                />
                <Typography variant="body2" color="text.secondary">
                  Adds this app to your personal navigation. Other users add it
                  themselves.
                </Typography>
              </Box>
            )}
          </Stack>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setEditing(null)}>Cancel</Button>
          <Button
            variant="contained"
            disabled={savingEdit || !editDraft.name.trim()}
            onClick={onSaveEdit}
          >
            {savingEdit ? "Saving…" : "Save"}
          </Button>
        </DialogActions>
      </Dialog>

      <IconPickerDialog
        open={iconPickerOpen}
        value={
          iconTarget === "edit" ? editDraft.icon : createDraft.icon
        }
        onClose={() => {
          setIconPickerOpen(false);
          setIconTarget(null);
        }}
        onSelect={(name) => {
          if (iconTarget === "edit") {
            setEditDraft((d) => ({ ...d, icon: name }));
          } else {
            setCreateDraft((d) => ({ ...d, icon: name }));
          }
        }}
      />
    </Box>
  );
};

export default AppsList;
